import React, { useContext, useState } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { AsyncState, ConfirmationDialog } from '../components';
import { normalizeAdminApiError } from '../errors';
import { can } from '../permissions';
import { DeviceSummary } from './DeviceSummary';
import { useCurrentDevice, useDeviceAction } from './hooks';
import type { BootstrapData } from './types';
import './devices.css';

const isBootstrapData = (value: unknown): value is BootstrapData => Boolean(
    value && typeof value === 'object'
    && typeof (value as BootstrapData).bootstrapToken === 'string'
    && typeof (value as BootstrapData).expiresAt === 'string',
);

const secondaryButton = `
    inline-flex h-9 items-center justify-center rounded-[9px] border border-slate-200
    bg-white px-3.5 text-[12px] font-semibold text-slate-700 transition
    hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-200
    disabled:cursor-not-allowed disabled:opacity-45
`;

const primaryButton = `
    inline-flex h-9 items-center justify-center rounded-[9px] border border-transparent
    bg-[#0d162a] px-3.5 text-[12px] font-semibold text-white transition
    hover:bg-[#172239] focus:outline-none focus:ring-2 focus:ring-slate-300
    disabled:cursor-not-allowed disabled:opacity-45
`;

const dangerButton = `
    inline-flex h-9 items-center justify-center rounded-[9px] border border-red-200
    bg-white px-3.5 text-[12px] font-semibold text-red-600 transition
    hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-100
    disabled:cursor-not-allowed disabled:opacity-45
`;

export function CurrentDeviceCard({ studentId }: { studentId: string }) {
    const { user } = useContext(AuthContext);
    const permitted = can(user?.role, 'mobile:manage');
    const query = useCurrentDevice(studentId, permitted);
    const mutation = useDeviceAction(studentId);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
    const [copied, setCopied] = useState(false);

    if (!permitted) return null;

    const device = query.data?.data.device ?? null;
    const error = query.error ? normalizeAdminApiError(query.error) : null;
    const mutationError = mutation.error ? normalizeAdminApiError(mutation.error) : null;

    const createBootstrap = () => mutation.mutate(
        { action: 'bootstrap' },
        {
            onSuccess: response => {
                if (isBootstrapData(response.data)) setBootstrap(response.data);
            },
        },
    );

    const copy = async () => {
        if (bootstrap && navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(bootstrap.bootstrapToken);
                setCopied(true);
            } catch {
                setCopied(false);
            }
        }
    };

    return (
        <section
            className="overflow-hidden rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
            aria-labelledby="current-device-title"
        >
            <div className="border-b border-slate-100 px-5 py-4">
                <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">One active device policy</p>
                <h2 id="current-device-title" className="mb-0 mt-1 text-[17px] font-bold text-slate-950">Current Device</h2>
            </div>

            <div className="p-5">
                <AsyncState loading={query.isLoading} error={error} onRetry={() => query.refetch()} empty={false}>
                    <div className="rounded-[13px] border border-slate-200 bg-slate-50/45 p-4 text-[13px] text-slate-700 [&_code]:rounded-md [&_code]:bg-slate-100 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[11px] [&_small]:text-[11px] [&_small]:text-slate-500 [&_strong]:font-semibold [&_strong]:text-slate-900">
                        <DeviceSummary device={device} />
                    </div>
                </AsyncState>

                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                    {device ? (
                        <button className={dangerButton} type="button" onClick={() => setConfirmRevoke(true)}>
                            Revoke device
                        </button>
                    ) : (
                        <button className={primaryButton} type="button" onClick={createBootstrap} disabled={mutation.isPending}>
                            {mutation.isPending ? 'Creating…' : 'Create device OTP'}
                        </button>
                    )}
                </div>

                {mutationError && (
                    <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-[12px] font-medium text-red-700" role="alert">
                        {mutationError.code === 'DEVICE_TRANSACTION_UNAVAILABLE'
                            ? 'Device changes are temporarily unavailable. No fallback action was taken.'
                            : mutationError.message}
                    </div>
                )}

                {bootstrap && (
                    <div className="mt-4 rounded-[13px] border border-blue-200 bg-blue-50/50 p-4" role="status">
                        <strong className="text-[13px] font-bold text-slate-900">One-time device OTP</strong>
                        <p className="mb-3 mt-1 text-[12px] leading-5 text-slate-500">
                            Share through the approved support channel. It disappears after dismissal.
                        </p>
                        <code className="block overflow-x-auto rounded-lg border border-blue-100 bg-white px-3 py-2 text-[12px] font-semibold text-slate-800">
                            {bootstrap.bootstrapToken}
                        </code>
                        <span className="mt-2 block text-[11px] text-slate-500">
                            Expires {new Date(bootstrap.expiresAt).toLocaleString()}
                        </span>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button className={primaryButton} type="button" onClick={copy}>
                                {copied ? 'Copied' : 'Copy OTP'}
                            </button>
                            <button
                                className={secondaryButton}
                                type="button"
                                onClick={() => {
                                    setBootstrap(null);
                                    setCopied(false);
                                }}
                            >
                                Dismiss permanently
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ConfirmationDialog
                open={confirmRevoke}
                title="Revoke active device"
                description="This revokes the device, all active sessions, and pending device-change requests. Password-only replacement remains forbidden."
                confirmLabel="Revoke device"
                confirmationText="REVOKE DEVICE"
                requireReason
                danger
                busy={mutation.isPending}
                onCancel={() => setConfirmRevoke(false)}
                onConfirm={({ reason }) => {
                    mutation.mutate({ action: 'revoke', reason });
                    setConfirmRevoke(false);
                }}
            />
        </section>
    );
}
